﻿import { LitElement, html, css } from 'lit';

export class PdfViewer extends LitElement {
    static properties = {
        base64: { type: String },
        zoom: { type: Number },
        translateX: { type: Number },
        translateY: { type: Number }
    };

    static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      overflow: auto;
      touch-action: none;
      position: relative;
    }

    #zoomContainer {
      transform-origin: 0 0;
      transition: transform 0.05s linear;
      will-change: transform;
    }

    canvas {
      display: block;
      max-width: 100%;
    }
  `;

    constructor() {
        super();
        this.zoom = 1.0;
        this.translateX = 0;
        this.translateY = 0;
        this._startDistance = null;
        this._startZoom = 1.0;
        this._dragStart = null;
        this._startTranslate = { x: 0, y: 0 };
    }

    firstUpdated() {
        this.canvas = this.shadowRoot.querySelector('canvas');
        const ctx = this.canvas.getContext('2d');

        this.addEventListener('pointerdown', this._onPointerDown.bind(this));
        this.addEventListener('pointermove', this._onPointerMove.bind(this));
        this.addEventListener('pointerup', this._onPointerUp.bind(this));
        this.addEventListener('pointercancel', this._onPointerUp.bind(this));

        // Decode base64 PDF

        const raw = atob(this.base64);
        const byteArray = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) byteArray[i] = raw.charCodeAt(i);

        pdfjsLib.getDocument({ data: byteArray }).promise
            .then(pdf => pdf.getPage(1))
            .then(page => {
                const viewport = page.getViewport({ scale: 1.5 }); // Basisgröße
                this.canvas.width = viewport.width;
                this.canvas.height = viewport.height;
                page.render({ canvasContext: ctx, viewport });



            });

        // Touch-Events für Pinch-to-Zoom
        this.addEventListener('touchstart', this._onTouchStart.bind(this), { passive: false });
        this.addEventListener('touchmove', this._onTouchMove.bind(this), { passive: false });

        // Optional: Mouse wheel zoom
        this.addEventListener('wheel', this._onWheel.bind(this), { passive: false });
    }

    _onPointerDown(e) {
        if (e.pointerType === 'touch' || e.pointerType === 'mouse') {
            this._dragStart = { x: e.clientX, y: e.clientY };
            this._startTranslate = { x: this.translateX, y: this.translateY };
            this.setPointerCapture(e.pointerId);
        }
    }

    _onPointerMove(e) {
        if (this._dragStart) {
            const dx = e.clientX - this._dragStart.x;
            const dy = e.clientY - this._dragStart.y;
            this.translateX = this._startTranslate.x + dx / this.zoom;
            this.translateY = this._startTranslate.y + dy / this.zoom;
        }
    }

    _onPointerUp(e) {
        this._dragStart = null;
        this.releasePointerCapture(e.pointerId);
    }

    _onTouchStart(e) {
        if (e.touches.length === 2) {
            e.preventDefault();
            this._startDistance = this._getDistance(e.touches[0], e.touches[1]);
            this._startZoom = this.zoom;
        }
    }

    _onTouchMove(e) {
        if (e.touches.length === 2 && this._startDistance) {
            e.preventDefault();
            const dist = this._getDistance(e.touches[0], e.touches[1]);
            const scaleChange = dist / this._startDistance;
            this.zoom = Math.max(0.5, Math.min(this._startZoom * scaleChange, 4));
        }
    }

    _onWheel(e) {
        e.preventDefault();
        const factor = e.deltaY < 0 ? 1.1 : 0.9;
        this.zoom = Math.max(0.5, Math.min(this.zoom * factor, 4));
    }

    _getDistance(t1, t2) {
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    render() {
        const transform = `scale(${this.zoom}) translate(${this.translateX}px, ${this.translateY}px)`;

        return html`
      <div id="zoomContainer" style="transform: ${transform}">
        <canvas></canvas>
      </div>
    `;
    }
}

customElements.define('pdf-viewer', PdfViewer);